import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  Tab,
  Tabs,
  Typography,
  IconButton,
  Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import PeopleIcon from '@mui/icons-material/People';
import InfoIcon from '@mui/icons-material/Info';
import { FaChessKnight } from 'react-icons/fa';

import { getTournament } from '../../../services/tournament.service';
import TournamentInfo from './TournamentInfo';
import TournamentPlayers from './TournamentPlayers';
import TournamentRounds from './TournamentRounds';
import TournamentTeamsRounds from './TournamentTeamsRounds';
import TournamentTeams from './TournamentTeams';



const TabPanel = ({ children, value, index }) => (
  <Box
    role="tabpanel"
    hidden={value !== index}
    sx={{ display: value === index ? 'block' : 'none' }}
  >
    {value === index && (
      <Box
        sx={{
          background: '#22252c',
          border: '1px solid rgba(255,255,255,0.08)',
          borderTop: 'none',
          borderRadius: 0,
          p: { xs: 2, md: 3 },
          boxShadow: '0 8px 32px rgba(0,0,0,0.24)',
        }}
      >
        {children}
      </Box>
    )}
  </Box>
);


const TournamentDetailPage = () => {
  const { torId } = useParams();
  const navigate = useNavigate();
  const [torneo, setTorneo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [tabIndex, setTabIndex] = useState(0);

  useEffect(() => {
    const fetchTorneo = async () => {
      try {
        const data = await getTournament(torId);
        setTorneo(data);
      } catch (e) {
        console.error(e);
      } finally {
        setIsLoading(false);
      }
    };
    fetchTorneo();
  }, [torId]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress sx={{ color: '#7c8cf8' }} />
      </Box>
    );
  }



  return (
    <Box sx={{ px: { xs: 2, md: 4 }, py: 3, maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Tooltip title="Volver a Torneos">
          <IconButton
            onClick={() => navigate('/tournaments')}
            sx={{
              color: '#9aa5b4',
              '&:hover': { color: '#7c8cf8', background: 'rgba(124,140,248,0.1)' },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>

        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
            <EmojiEventsIcon sx={{ color: '#86874f', fontSize: 28 }} />
            <Typography
              variant="h4"
              sx={{
                color: '#e2e8f0',
                fontWeight: 700,
                lineHeight: 1.2,
                fontSize: '1.5rem',
                fontFamily: '"Archivo Narrow", sans-serif',
                letterSpacing: '0.5px',
                textTransform: 'uppercase'
              }}
            >
              {torneo?.tor_nombre ?? 'Torneo'}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Tabs */}
      <Box
        sx={{
          background: '#22252c',
          borderRadius: 0,
          p: 0.75,
          border: '1px solid rgba(255,255,255,0.08)',
          borderBottom: 'none',
        }}
      >
        <Tabs
          value={tabIndex}
          onChange={(_, v) => setTabIndex(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTabs-indicator': { display: 'none' },
            '& .MuiTab-root': {
              color: '#9aa5b4',
              fontWeight: 600,
              textTransform: 'none',
              fontSize: '0.95rem',
              borderRadius: 0,
              minHeight: 40,
              py: 1,
              px: 2.5,
              gap: 1,
              mr: 1,
              border: '1px solid rgba(255,255,255,0.12)',
              background: 'rgba(255,255,255,0.02)',
              transition: 'all 0.2s ease',
              '&.Mui-selected': {
                color: '#ffffff',
                background: '#4d586b',
                borderColor: 'rgba(255,255,255,0.3)',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
              },
              '&:hover': {
                color: '#ffffff',
                background: 'rgba(255,255,255,0.08)',
                borderColor: 'rgba(255,255,255,0.25)',
              },
              '&.Mui-selected:hover': {
                background: '#4d586b',
                borderColor: 'rgba(255,255,255,0.3)',
              }
            },
          }}
        >
          <Tab icon={<InfoIcon fontSize="small" />} iconPosition="start" label="Datos del torneo" id="tab-0" />
          <Tab 
            icon={<PeopleIcon fontSize="small" />} 
            iconPosition="start" 
            label={torneo?.tor_tipo === 5 ? 'Equipos' : 'Jugadores'} 
            id="tab-1" 
          />
          <Tab icon={<FaChessKnight size={16} />} iconPosition="start" label="Partidas por ronda" id="tab-2" />
        </Tabs>
      </Box>

      <TabPanel value={tabIndex} index={0}>
        <TournamentInfo torneo={torneo} />
      </TabPanel>

      <TabPanel value={tabIndex} index={1}>
        {torneo?.tor_tipo === 5 ? (
          <TournamentTeams torId={torId} />
        ) : (
          <TournamentPlayers torId={torId} />
        )}
      </TabPanel>

      <TabPanel value={tabIndex} index={2}>
        {torneo?.tor_tipo === 5 ? (
          <TournamentTeamsRounds torId={torId} />
        ) : (
          <TournamentRounds torId={torId} />
        )}
      </TabPanel>
    </Box>
  );
};

export default TournamentDetailPage;
